/**
 * Skeletons.jsx
 *
 * Skeletons de chargement fidèles à la géométrie réelle des cards qu'ils
 * remplacent — même structure DOM (classes mc-, mlc-, pr- réutilisées pour
 * les dimensions/spacing), seules les zones de contenu deviennent des
 * rectangles "os" (.skl-bone) avec shimmer (voir styles/skeletons.css).
 *
 * Usage : pendant le chargement, afficher Array.from({ length: N })
 * et mapper chaque entrée vers le composant Skeleton correspondant
 * (ModernCardSkeleton, PropositionCardSkeleton, MissionListCardSkeleton,
 * KpiCardSkeleton), à la place des vraies cards.
 */
import React from 'react';

/* ════════════════════════════════════════════════════
   ModernCardSkeleton — réplique ModernAnnonceCard (.mc-card)
   Utilisé sur AnnoncesPage (/annonces)
════════════════════════════════════════════════════ */
export function ModernCardSkeleton({ index = 0 }) {
  // Alterne teal/navy comme les vraies cards colis/trajet, pour que la
  // grille de skeletons ait déjà le bon rythme visuel avant chargement.
  const accent = index % 2 === 0 ? 'teal' : 'navy';
  return (
    <div className={`mc-card mc-card--${accent} skl-card`} aria-hidden="true" data-testid="mcard-skeleton">
      <div className="mc-rail">
        <div className="mc-rail-icon skl-bone skl-bone--icon" />
        <div className="skl-bone skl-bone--rail-label" />
      </div>
      <div className="mc-body">
        <div className="mc-top-row">
          <div className="mc-badges">
            <span className="skl-bone skl-bone--badge" />
          </div>
          <span className="skl-bone skl-bone--fav" />
        </div>
        <div className="mc-route">
          <span className="skl-bone skl-bone--city" />
          <span className="skl-bone skl-bone--arrow" />
          <span className="skl-bone skl-bone--city skl-bone--city-sm" />
        </div>
        <div className="mc-meta">
          <span className="skl-bone skl-bone--chip" />
          <span className="skl-bone skl-bone--chip" />
          <span className="skl-bone skl-bone--chip" />
        </div>
        <div className="mc-footer">
          <div className="mc-author">
            <span className="skl-bone skl-bone--avatar" />
            <div className="mc-author-info">
              <span className="skl-bone skl-bone--name" />
              <span className="skl-bone skl-bone--rating" />
            </div>
          </div>
          <span className="skl-bone skl-bone--price" />
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   PropositionCardSkeleton — réplique PropositionCard (.pr-card)
   Utilisé sur PropositionsPage (/app/propositions)
════════════════════════════════════════════════════ */
export function PropositionCardSkeleton() {
  return (
    <div className="pr-card pr-status-muted skl-card" aria-hidden="true" data-testid="proposition-skeleton">
      <div className="pr-head">
        <span className="skl-bone skl-bone--av-round" style={{ animationDelay: '0ms' }} />
        <div className="pr-head-text">
          <span className="skl-bone skl-bone--name"  style={{ animationDelay: '60ms' }} />
          <span className="skl-bone skl-bone--sub"   style={{ animationDelay: '120ms' }} />
        </div>
        <div className="pr-head-meta">
          <span className="skl-bone skl-bone--badge-pill" style={{ animationDelay: '180ms' }} />
          <span className="skl-bone skl-bone--badge-pill" style={{ width: '54px', animationDelay: '230ms' }} />
        </div>
      </div>
      <div className="pr-route">
        <span className="skl-bone skl-bone--route-line" style={{ animationDelay: '80ms' }} />
      </div>
      <div className="pr-foot">
        <div className="pr-price">
          <span className="skl-bone skl-bone--label-sm" style={{ animationDelay: '140ms' }} />
          <span className="skl-bone skl-bone--price"    style={{ animationDelay: '190ms' }} />
        </div>
        <span className="skl-bone skl-bone--time" style={{ animationDelay: '230ms' }} />
      </div>
      <div className="pr-actions">
        <span className="skl-bone skl-bone--btn"                         style={{ animationDelay: '40ms' }} />
        <span className="skl-bone skl-bone--btn"                         style={{ animationDelay: '100ms' }} />
        <span className="skl-bone skl-bone--btn skl-bone--btn-wide"      style={{ animationDelay: '160ms' }} />
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   MissionListCardSkeleton — réplique MissionListCard (.mlc)
   Utilisé sur /app/missions, /app/shipments, /app/trips,
   et le widget "Missions récentes" du Dashboard (compact)
════════════════════════════════════════════════════ */
export function MissionListCardSkeleton({ compact = false }) {
  return (
    <div className={`mlc skl-card${compact ? ' mlc--compact' : ''}`} aria-hidden="true" data-testid="mlc-skeleton">
      <div className="mlc-head">
        <div className="mlc-route-wrap">
          <span className="skl-bone skl-bone--route-line" />
          <span className="skl-bone skl-bone--sub" />
        </div>
        <span className="skl-bone skl-bone--badge-pill" />
      </div>
      <div className="mlc-chips">
        <span className="skl-bone skl-bone--chip" />
        <span className="skl-bone skl-bone--chip" />
        <span className="skl-bone skl-bone--chip" />
      </div>
      <div className="mlc-foot">
        <span className="skl-bone skl-bone--price" />
        <div className="mlc-actions">
          <span className="skl-bone skl-bone--icn" />
          <span className="skl-bone skl-bone--icn" />
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   KpiCardSkeleton — réplique KpiCard (.kpi-card) du Dashboard
════════════════════════════════════════════════════ */
export function KpiCardSkeleton() {
  return (
    <div className="kpi-card skl-card" aria-hidden="true" data-testid="kpi-skeleton">
      <span className="skl-bone skl-bone--kpi-icon" />
      <span className="skl-bone skl-bone--label-sm" />
      <span className="skl-bone skl-bone--kpi-value" />
    </div>
  );
}
